/**
 * @file - Env service.
 */
import { Injectable } from '@angular/core';

/**
 * Keys written into window.__env by entrypoint.ci.sh / entrypoint.local.sh.
 * Keeping this as a fixed interface (rather than Record<string, string>)
 * means a typo'd or renamed key fails `nx build` instead of silently
 * resolving to `undefined` at runtime.
 */
interface RuntimeEnv {
    STRIPE_PUB_KEY: string;
    STRIPE_PUB_KEY_CANADA: string;
    CAPTCHA_KEY: string;
    PRIZEOUT_PARTNER_ID_US: string;
    PRIZEOUT_PARTNER_ID_CA: string;
    PRIZEOUT_PARTNER_KEY_US: string;
    PRIZEOUT_PARTNER_KEY_CA: string;
    GOOGLE_PLACES_API_KEY: string;
    MAINTENANCE_MODE_ACTIVE_FLAG: string;
    VERSION: string;
}

// Declared as a loose Record (not Partial<RuntimeEnv>) because `declare
// global` augments the single, program-wide Window type - every app's
// env.service.ts must declare window.__env identically, since each has
// its own differently-shaped RuntimeEnv. The stricter shape is applied
// internally in `runtimeEnv` below instead.
declare global {
    interface Window {
        __env?: Record<string, string>;
    }
}

/**
 * Reads runtime secret/config values from `window.__env`, populated by
 * env.js (written by entrypoint.ci.sh at container start, or by
 * entrypoint.local.sh for local dev, before the app bundle loads).
 */
@Injectable({
    providedIn: 'root',
})
export class EnvService {
    /**
     * Runtime env values from window.__env, or an empty object if unset.
     */
    private get runtimeEnv(): Partial<RuntimeEnv> {
        return (window.__env as Partial<RuntimeEnv>) ?? {};
    }

    /**
     * Returns the value for a required runtime env key, throwing if it's
     * missing. A missing value here means env.js was not written
     * correctly (see entrypoint.ci.sh / entrypoint.local.sh), which should
     * fail loudly rather than silently break the feature reading it.
     *
     * @param {K} key - Runtime env key.
     * @returns {string} - Runtime env value.
     */
    private require<K extends keyof RuntimeEnv>(key: K): string {
        const value = this.runtimeEnv[key];
        if (!value) {
            throw new Error(
                `EnvService: missing runtime env value for "${key}"`
            );
        }
        return value;
    }

    /**
     * reCAPTCHA site key.
     */
    get captchaKey(): string {
        return this.require('CAPTCHA_KEY');
    }

    /**
     * Google Places API key, used for the Google Maps JS API script.
     */
    get googlePlacesApiKey(): string {
        return this.require('GOOGLE_PLACES_API_KEY');
    }

    /**
     * Stripe publishable key for US accounts.
     */
    get stripePubKey(): string {
        return this.require('STRIPE_PUB_KEY');
    }

    /**
     * Stripe publishable key for Canada accounts.
     */
    get stripePubKeyCanada(): string {
        return this.require('STRIPE_PUB_KEY_CANADA');
    }

    /**
     * Build/version identifier, reported to Datadog RUM and sent as the
     * X-WebFrontend-Version request header. Degrades quietly to an empty
     * string rather than throwing, since it's cosmetic/diagnostic only.
     */
    get version(): string {
        return this.runtimeEnv['VERSION'] ?? '';
    }

    /**
     * Whether the app should render in maintenance mode. Degrades quietly
     * to an empty string (treated as "not in maintenance mode" by
     * callers) rather than throwing, since it's a display flag, not a
     * security-relevant secret.
     */
    get maintenanceModeActiveFlag(): string {
        return this.runtimeEnv['MAINTENANCE_MODE_ACTIVE_FLAG'] ?? '';
    }

    /**
     * Prizeout partner ID for the given country code (e.g. 'US', 'CA').
     *
     * @param {string} country - Country code.
     * @returns {string} - Prizeout partner ID.
     */
    getPrizeoutPartnerId(country: string): string {
        return this.require(`PRIZEOUT_PARTNER_ID_${country}` as keyof RuntimeEnv);
    }

    /**
     * Prizeout partner API key for the given country code (e.g. 'US', 'CA').
     *
     * @param {string} country - Country code.
     * @returns {string} - Prizeout partner API key.
     */
    getPrizeoutPartnerKey(country: string): string {
        return this.require(`PRIZEOUT_PARTNER_KEY_${country}` as keyof RuntimeEnv);
    }
}
